/* eslint-disable react/jsx-props-no-spreading */
import React, { useMemo } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import { components } from 'react-select';
import i18n from 'i18next';
import Add from '@mui/icons-material/Add';
import Check from '@mui/icons-material/Check';
import { useModals } from 'core/notifications';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';

import { Select } from 'uikit/selects';
import { Typo } from 'uikit/content';

import getVersioningStatusString from '../helpers/getVersioningStatusString';

import useGlobalQueries from '../hooks/useGlobalQueries';

import getNewLocaleSchema from '../schemas/getNewLocaleSchema';

import NewItemFormContainer from '../containers/newItemFormContainer';

import styles from '../styles/versioningToolbarLocaleSelect.module.scss';

const getIsAddButton = (value) => value === 'ADD_LOCALE';

const Control = (props) => <components.Control className={styles.control} {...props} />;

const Menu = (props) => <components.Menu className={styles.menu} {...props} />;

const Option = (props) => {
  const { value } = props;
  const {
    is_published: isPublished,
    published_at: publishedAt,
    version_state_updated_at: updatedAt,
  } = value;

  return (
    <components.Option className={styles.option} {...props}>
      {getIsAddButton(value) ? (
        <div className={styles.add}>
          <Add sx={{ height: 18, width: 18 }} />
          <Typo variant="s02">{props.label}</Typo>
        </div>
      ) : (
        <>
          <div className={classnames(styles.inner, styles.isColumn)}>
            <Typo variant="s02">{props.label}</Typo>
            <div className={styles.body}>
              <Typo variant="s01" color="secondary">
                {getVersioningStatusString({ isPublished, publishedAt, updatedAt })}
              </Typo>
              {value.has_unpublished_changes && (
                <div className={styles.inner}>
                  <WarningAmberOutlined sx={{ height: 14, width: 14 }} />
                  <Typo variant="s01">{i18n.t('unpublished-changes')}</Typo>
                </div>
              )}
            </div>
          </div>
          {props.isSelected && <Check sx={{ height: 18, width: 18 }} />}
        </>
      )}
    </components.Option>
  );
};

const VersioningToolbarLocaleSelect = ({
  getNewLocaleAxiosConfig = () => {},
  isDisabled = false,
  locale = null,
  locales = [],
  onAdd = () => {},
  onChange = () => {},
}) => {
  const { addModal } = useModals();
  const localeLabels = useGlobalQueries().adminSettings.data.locales;

  const options = useMemo(
    () => [
      ...locales.map((option) => ({
        label: localeLabels?.find(({ key }) => key === option.locale)?.name,
        value: option,
      })),
      { label: i18n.t('add-locale'), value: 'ADD_LOCALE' },
    ],
    [localeLabels, locales],
  );

  const handleAddLocaleClick = async () => {
    const id = 'new-locale';
    const addLocaleString = i18n.t('add-locale');

    const { payload, type } = await addModal({
      component: (
        <NewItemFormContainer
          getAxiosConfig={getNewLocaleAxiosConfig}
          id={id}
          isFullScreen
          schema={getNewLocaleSchema({ localeLabels, locales })}
          submitText={addLocaleString}
        />
      ),
      hasCloseButton: true,
      id,
      isFullScreen: true,
      title: addLocaleString,
    });

    if (type === 'submit') {
      onAdd(payload);
    }
  };

  const handleChange = ({ value }) => {
    if (getIsAddButton(value)) {
      handleAddLocaleClick();
    } else {
      onChange(value);
    }
  };

  return (
    <Select
      components={{ Control, Menu, Option }}
      isDisabled={isDisabled}
      onChange={handleChange}
      options={options}
      size="small"
      value={options.find(({ value }) => value.locale === locale)}
    />
  );
};

VersioningToolbarLocaleSelect.propTypes = {
  getNewLocaleAxiosConfig: PropTypes.func,
  isDisabled: PropTypes.bool,
  locale: PropTypes.string,
  locales: PropTypes.arrayOf(
    PropTypes.shape({
      has_unpublished_changes: PropTypes.bool,
      id: PropTypes.number,
      is_published: PropTypes.bool,
      locale: PropTypes.string,
      published_at: PropTypes.string,
      version_state_updated_at: PropTypes.string,
    }),
  ),
  onAdd: PropTypes.func,
  onChange: PropTypes.func,
};

export default VersioningToolbarLocaleSelect;
